import { RumEvent } from '@datadog/browser-rum';

export const RUM_RESOURCE_TRACKING_KEY = 'rs-bp.rumResourceTracking';

/**
 * EnableRumResourceTracking as last saved. beforeSend can't wait for the flag fetch, so each page load starts
 * from the stored value; none means off.
 */
let resourceTrackingEnabled = readStoredFlag();

function readStoredFlag(): boolean {
    try {
        return localStorage.getItem(RUM_RESOURCE_TRACKING_KEY) === 'true';
    } catch {
        return false; // storage blocked: send no resources
    }
}

export function setRumResourceTracking(enabled: boolean): void {
    resourceTrackingEnabled = enabled;
    try {
        localStorage.setItem(RUM_RESOURCE_TRACKING_KEY, String(enabled));
    } catch {
        // storage blocked: the next page load starts with resources off
    }
}

/**
 * Static path words of the portal's API URLs; any other segment is masked to `_`. Kept complete by
 * scripts/check-rum-path-words.js.
 */
export const PORTAL_PATH_WORDS = [
    'accreditation',
    'actions',
    'additionalResource',
    'air-conditioners',
    'aircon-units',
    'amortisation-schedule',
    'api',
    'applicant',
    'application',
    'applications',
    'application-summary',
    'approvalconditions',
    'approvalcreditconditions',
    'authentication',
    'balloonPayment',
    'bankDetails',
    'BankStatements',
    'biometric',
    'boolean',
    'broker',
    'brokers',
    'brokerSettingInfo',
    'builders',
    'by-quote',
    'bystate',
    'cancel',
    'caspio',
    'CecInstallers',
    'cecpackages',
    'CecPackages',
    'confirmInstallation',
    'confirmInstallationAddress',
    'confirmInstallerDetails',
    'confirmPaymentDetailsInstallation',
    'confirmSystemDetails',
    'confirm-uploaded-document',
    'contact',
    'content',
    'credit',
    'CreditEvaluation',
    'creditScore',
    'customer',
    'dashboard',
    'dealershipverification',
    'details',
    'document',
    'documents',
    'dynamic',
    'enablePaperlessFallback',
    'experience',
    'ext',
    'extrafields',
    'feature',
    'financeapplication',
    'FinanceQuote',
    'financeSummary',
    'freetext',
    'greenconnect',
    'guides',
    'heat-pumps',
    'installer-licence',
    'internal',
    'latest',
    'leads',
    'library',
    'loanAmount',
    'loandrawers',
    'logout',
    'LowestEstimatedRepayments',
    'Manifest',
    'messaging',
    'monthly',
    'monthlystats',
    'nmi-availability',
    'oauth2',
    'offer-config-options',
    'offers',
    'OriginationTiers',
    'packages',
    'partner',
    'PartnerService',
    'payout-letter',
    'payout-letter-eligibility',
    'platform',
    'portal',
    'postcodes',
    'pricing',
    'product',
    'product-guides',
    'productpackages',
    'Profile',
    'providerconfirmation',
    'quote',
    'quotebuilders',
    'quotecancellation',
    'quotes',
    'rates',
    'Rates',
    'recover',
    'reference-data',
    'referrals',
    'refreshtoken',
    'regenerateRemittance',
    'renewal',
    'reporting',
    'resendConsent',
    'resendEntitySMS',
    'resend-notification',
    'resendSMS',
    'resetIdentity',
    'resetpassword',
    'revoke',
    'schemeCoverage',
    'search',
    'secured',
    'sendPayoutLetter',
    'sendTransactionsListing',
    'settingGroups',
    'settings',
    'solar-water-heaters',
    'status',
    'steps',
    'submitconditions',
    'subsidy',
    'SubsidyPrices',
    'SubventionPartner',
    'supplier',
    'toggleDisplayAprInContract',
    'token',
    'transaction-listing',
    'Type',
    'update',
    'updateBalloonPayment',
    'updateBrokerage',
    'updateBrokerRateDiscount',
    'updateCommercialBalloonPayment',
    'updateCommercialBorrowerDetails',
    'updateExtraFields',
    'updateLoanAmount',
    'updateLoanAmountAndTerm',
    'updateLoanTermType',
    'updateRepaymentStructure',
    'updateStatus',
    'updateVehicleDetails',
    'upload',
    'user-demand',
    'user-mfa',
    'users',
    'v2',
    'validate',
    'validateBsb',
    'validatedobject',
    'validateNmi',
    'verification',
    'verimoto',
    'workflow',
    'WorkflowService',
    'zohosign'
];

/** Used only by the BrokerPortalReactUi micro-UIs, which the CI check can't read: maintained by hand. */
const MICRO_UI_PATH_WORDS = ['requirements', 'resendRequirementEmail'];

const API_PATH_WORDS = new Set([...PORTAL_PATH_WORDS, ...MICRO_UI_PATH_WORDS]);

/** Segments after `search` are always masked: the term is free text, isn't URL-encoded and can span segments. */
export function maskApiUrl(url: URL): string {
    let afterSearch = false;
    const path = url.pathname
        .split('/')
        .map(segment => {
            const keep = segment === '' || (!afterSearch && API_PATH_WORDS.has(segment));
            afterSearch = afterSearch || segment === 'search';
            return keep ? segment : '_';
        })
        .join('/');
    return url.origin + path;
}

/**
 * Returns false to drop. Flag off also drops the SDK's events for traced calls, which APM builds browser spans
 * from.
 */
export function keepRumResource(event: RumEvent, apiOrigins: string[]): boolean {
    if (event.type !== 'resource') {
        return true;
    }
    if (!resourceTrackingEnabled || (event.resource.type !== 'xhr' && event.resource.type !== 'fetch')) {
        return false;
    }

    let url: URL;
    try {
        url = new URL(event.resource.url);
    } catch {
        return false;
    }
    // Parsed origins normalise default ports such as `:443`.
    if (!apiOrigins.includes(url.origin)) {
        return false;
    }

    event.resource.url = maskApiUrl(url);
    return true;
}
